import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { State, Store } from '@ngrx/store';
import {
  AutoPairGlobalConfiguration,
  AutoPairingService,
  AutoPairTransitProgramConfiguration,
  OperationalFootprintConfiguration,
  OperationalFootprintInfo,
  TransitProgramService
} from '@uzurv/clara';
import { isNullOrUndefined } from '@uzurv/core';
import { AuthDataService } from '@uzurv/http';
import { Deserialize } from 'cerialize';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AlertDialogComponent } from '../../components/dialogs/alert-dialog/alert-dialog.component';
import * as AutopairingActions from './autopairing.actions';
import { AutopairingState } from './autopairing.reducer';
import * as AutopairingSelectors from './autopairing.selectors';

@Injectable()
export class AutopairingEffects {
  constructor(
    private actions$: Actions,
    private store$: Store<State<AutopairingState>>,
    private authDataService: AuthDataService,
    private autoPairingService: AutoPairingService,
    private dialog: MatDialog,
    private transitProgramService: TransitProgramService
  ) {}

  error$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          AutopairingActions.GetAutopairingGlobalConfigFail,
          AutopairingActions.SaveAutopairingGlobalConfigFail,
          AutopairingActions.GetTransitProgramsFail,
          AutopairingActions.SearchTransitProgramsFail,
          AutopairingActions.GetOperationalFootprintsFail
        ),
        map(action => {
          this.dialog.open(AlertDialogComponent, {
            data: {
              title: 'Error',
              message: action.error.message
            }
          });
        })
      );
    },
    { dispatch: false }
  );

  getGlobalConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutopairingActions.GetAutopairingGlobalConfig),
      concatLatestFrom(() => this.store$.select(AutopairingSelectors.getGlobalConfig)),
      filter(() => {
        return this.authDataService.hasPermission({
          any: ['auto_pairing_configuration']
        });
      }),
      switchMap(([action, globalConfig]) =>
        globalConfig.config === null
          ? this.autoPairingService.getGlobalConfiguration().pipe(
              map(configuration =>
                !isNullOrUndefined(configuration)
                  ? AutopairingActions.GetAutopairingGlobalConfigSuccess({
                      config: Deserialize(configuration, AutoPairGlobalConfiguration)
                    })
                  : AutopairingActions.GetAutopairingGlobalConfigSuccess({ config: null })
              ),
              catchError(error => of(AutopairingActions.GetAutopairingGlobalConfigFail({ error })))
            )
          : of(
              AutopairingActions.GetAutopairingGlobalConfigSuccess({
                config: globalConfig.config
              })
            )
      )
    )
  );

  saveGlobalConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AutopairingActions.SaveAutopairingGlobalConfig),
      filter(() => {
        return this.authDataService.hasPermission({
          any: ['auto_pairing_configuration']
        });
      }),
      switchMap(action =>
        this.autoPairingService.saveGlobalConfiguration(action.config).pipe(
          map(configuration =>
            AutopairingActions.SaveAutopairingGlobalConfigSuccess({
              config: Deserialize(configuration, AutoPairGlobalConfiguration)
            })
          ),
          catchError(error => of(AutopairingActions.SaveAutopairingGlobalConfigFail({ error })))
        )
      )
    )
  );

  // Operation Footprint

  getOperationalFootprints$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AutopairingActions.GetOperationalFootprints),
      withLatestFrom(this.store$.select(AutopairingSelectors.autopairingState)),
      switchMap(([_, state]) =>
        state.operationalFootprintState.operationalFootprints === null
          ? this.autoPairingService
              .getOperationalFootprints(state.operationalFootprintState.pageNumber, state.operationalFootprintState.pageSize)
              .pipe(
                map(operationalFootprints =>
                  !isNullOrUndefined(operationalFootprints)
                    ? AutopairingActions.GetOperationalFootprintsSuccess({
                        operationalFootprints: Deserialize(operationalFootprints, OperationalFootprintInfo)
                      })
                    : AutopairingActions.GetOperationalFootprintsSuccess({ operationalFootprints: null })
                ),
                catchError(error => of(AutopairingActions.GetOperationalFootprintsFail({ error })))
              )
          : of(
              AutopairingActions.GetOperationalFootprintsSuccess({
                operationalFootprints: state.operationalFootprintState.operationalFootprints
              })
            )
      )
    );
  });

  getOperationalFootprintConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(AutopairingActions.GetOperationalFootprintConfig),
      concatLatestFrom(() => this.store$.select(AutopairingSelectors.getOperationalFootprintConfig)),
      filter(() => {
        return this.authDataService.hasPermission({
          any: ['auto_pairing_configuration']
        });
      }),
      switchMap(([action, operationalFootprintConfig]) =>
        operationalFootprintConfig.config === null
          ? this.autoPairingService.getOperationalFootprintConfiguration(action.operationalFootprintId).pipe(
              map(configuration =>
                !isNullOrUndefined(configuration)
                  ? AutopairingActions.GetOperationalFootprintConfigSuccess({
                      config: Deserialize(configuration, OperationalFootprintConfiguration)
                    })
                  : AutopairingActions.GetOperationalFootprintConfigSuccess({ config: null })
              ),
              catchError(error => of(AutopairingActions.GetOperationalFootprintConfigFail({ error })))
            )
          : of(
              AutopairingActions.GetOperationalFootprintConfigSuccess({
                config: operationalFootprintConfig.config
              })
            )
      )
    )
  );

  saveOperationalFootprintConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(AutopairingActions.SaveAutopairingOperationalFootprintConfig),
      filter(() => {
        return this.authDataService.hasPermission({
          any: ['auto_pairing_configuration']
        });
      }),
      switchMap(action =>
        this.autoPairingService.saveOperationalFootprintConfiguration(action.operationalFootprintId, action.config).pipe(
          map(configuration =>
            AutopairingActions.SaveAutopairingOperationalFootprintConfigSuccess({
              config: Deserialize(configuration, OperationalFootprintConfiguration)
            })
          ),
          catchError(error => of(AutopairingActions.SaveAutopairingOperationalFootprintConfigFail({ error })))
        )
      )
    )
  );
  // Transit Program Effects

  getTransitPrograms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AutopairingActions.GetTransitPrograms, AutopairingActions.ClearSearch),
      withLatestFrom(this.store$.select(AutopairingSelectors.autopairingState)),
      switchMap(([action, state]) => {
        return this.transitProgramService
          .listAllTransitPrograms(
            state.transitProgramSearchState.transitProgramsPageIndex,
            state.transitProgramSearchState.transitProgramsPageSize,
            false
          )
          .pipe(
            map(transitPrograms => AutopairingActions.GetTransitProgramsSuccess({ transitPrograms })),
            catchError(error => of(AutopairingActions.GetTransitProgramsFail({ error })))
          );
      })
    );
  });

  searchTransitPrograms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AutopairingActions.SearchTransitPrograms),
      withLatestFrom(this.store$.select(AutopairingSelectors.autopairingState)),
      switchMap(([action, state]) =>
        this.transitProgramService
          .searchTransitPrograms(
            action.searchString,
            state.transitProgramSearchState.transitProgramsPageIndex,
            state.transitProgramSearchState.transitProgramsPageSize,
            false
          )
          .pipe(
            map(transitPrograms => AutopairingActions.SearchTransitProgramsSuccess({ transitPrograms })),
            catchError(error => of(AutopairingActions.SearchTransitProgramsFail({ error })))
          )
      )
    );
  });

  paginateTransitProgram$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AutopairingActions.PaginateTransitPrograms),
      withLatestFrom(this.store$.select(AutopairingSelectors.autopairingState)),
      map(([action, state]) => {
        return state.transitProgramSearchState.searchString?.length > 0
          ? AutopairingActions.SearchTransitPrograms({ searchString: state.transitProgramSearchState.searchString })
          : AutopairingActions.GetTransitPrograms();
      })
    );
  });

  // Transit Program Configuration

  getTransitProgramConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(AutopairingActions.GetAutopairingTransitProgramConfig),
      concatLatestFrom(() => this.store$.select(AutopairingSelectors.getTransitProgramConfig)),
      filter(() => {
        return this.authDataService.hasPermission({
          any: ['auto_pairing_configuration']
        });
      }),
      switchMap(([action, transitProgramConfig]) =>
        transitProgramConfig.config === null
          ? this.autoPairingService.getTransitProgramConfiguration(action.transitProgramId).pipe(
              map(configuration =>
                !isNullOrUndefined(configuration)
                  ? AutopairingActions.GetAutopairingTransitProgramConfigSuccess({
                      config: Deserialize(configuration, AutoPairTransitProgramConfiguration)
                    })
                  : AutopairingActions.GetAutopairingTransitProgramConfigSuccess({ config: null })
              ),
              catchError(error => of(AutopairingActions.GetAutopairingTransitProgramConfigFail({ error })))
            )
          : of(
              AutopairingActions.GetAutopairingTransitProgramConfigSuccess({
                config: transitProgramConfig.config
              })
            )
      )
    )
  );

  saveTransitProgramConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(AutopairingActions.SaveAutopairingTransitProgramConfig),
      filter(() => {
        return this.authDataService.hasPermission({
          any: ['auto_pairing_configuration']
        });
      }),
      switchMap(action =>
        this.autoPairingService.saveTransitProgramConfiguration(action.transitProgramId, action.config).pipe(
          map(configuration =>
            AutopairingActions.SaveAutopairingTransitProgramConfigSuccess({
              config: Deserialize(configuration, AutoPairTransitProgramConfiguration)
            })
          ),
          catchError(error => of(AutopairingActions.SaveAutopairingTransitProgramConfigFail({ error })))
        )
      )
    )
  );
}
